import type { PageInfoContent } from "components/common/PageHelpButton";

const actualsInfo: PageInfoContent = {
  title: "Actuals",
  description:
    "You are on the Actuals page, where Recon shows selected as-run data for the networks, dates, schedules, and feeds in your subscription.",
  dataShown: [
    "Selected network scopes with broadcast date, schedule, and feed purpose context.",
    "Show, break, and spot-level actuals with material, timing, event, billing, and aired details.",
    "Material type filters, load status, refresh state, and edit controls when your role allows them.",
  ],
  actions: [
    "Change the subscription to load a different actuals scope.",
    "Filter material types, switch between selected networks, and refresh loaded data.",
    "Review actual details and use bulk edit tools when you have edit access. For Aired updates, select Side X, Y, or X and Y before applying the change.",
  ],
};

const reportsInfo: PageInfoContent = {
  title: "Reports",
  description:
    "You are on the Reports page, where Recon lists the report builders available for operational review and export.",
  actions: [
    "Choose the report type that matches your review or export need.",
    "Open a report builder to select dates, networks, feeds, formats, and other options.",
    "Use generated reports outside Recon for reconciliation, planning, or validation.",
  ],
};

const reportsDownloadInfo: PageInfoContent = {
  title: "Download Reports",
  description:
    "You are on a report builder page, where Recon prepares downloadable report files from the selected broadcast scope.",
  dataShown: [
    "Report-specific fields such as broadcast dates, network, feed association, feed number, indicators, and file format.",
  ],
  actions: [
    "Select the report criteria and output format.",
    "Download PDF, Excel, or XML outputs when supported by the selected report.",
  ],
};

const breakinLogInfo: PageInfoContent = {
  title: "Breakin Log",
  description: "You are on the Breakin Log page, where Recon shows actual break-in records for review and maintenance.",
  dataShown: [
    "Break-in records with source, air channel, timing, timezone, and related operational details.",
    "Filters for broadcast date, source, air channel, timezone, deleted records, and reconciliation playback records.",
    "Add, update, or delete controls when your role allows changes.",
  ],
  actions: [
    "Filter the log to the records you need to review.",
    "Add a new break-in record when you have edit access.",
    "Review and maintain break-in entries used by reconciliation workflows.",
  ],
};

const compareToolInfo: PageInfoContent = {
  title: "Schedule Compare",
  description:
    "You are on the Schedule Compare page, where Recon compares schedule data and highlights differences that need review.",
  dataShown: [
    "Selected networks, broadcast dates, and schedule criteria for the comparison.",
    "Side-by-side schedule and as-run values with difference indicators.",
    "Show, break, and spot-level memo status, discrepancy filters, and export options when data is loaded.",
  ],
  actions: [
    "Run a comparison for the selected schedule scope.",
    "Display only the discrepancies found in shows.",
    "Add review memos and export comparison results when needed.",
  ],
};

const adminInfo: PageInfoContent = {
  title: "Admin Dashboard",
  description: "You are on the Admin Dashboard, where administrators manage user access for Recon.",
  dataShown: [
    "The user directory with SSO, name, email, and role information.",
    "Search results for matching users.",
    "Add and edit controls for maintaining user roles.",
  ],
  actions: [
    "Search for users by SSO, first name, last name, or email.",
    "Add new users to Recon access control.",
    "Review and update user roles when administrative changes are needed.",
  ],
};

const soundMouseInfo: PageInfoContent = {
  title: "SoundMouse",
  description:
    "You are on the SoundMouse POC page, where Recon prepares a production header from a selected network, broadcast date, and show.",
  dataShown: [
    "Networks available on the selected broadcast date that have a configured default channel.",
    "Recon shows for the selected network and broadcast date.",
    "The production metadata that will be sent to the configured SoundMouse workspace.",
    "All material types returned for the selected show in the media-file step.",
  ],
  actions: [
    "Select a network and broadcast date, then load available shows.",
    "Choose one show and review or copy the generated production JSON.",
    "Create or update the production in SoundMouse, or continue directly to the media-file workflow with the selected show.",
    "Review every returned material type and optionally limit the selection to rows included for billing.",
  ],
};

const pageInfoByPath: Record<string, PageInfoContent> = {
  "/": actualsInfo,
  "/actuals": actualsInfo,
  "/reports": reportsInfo,
  "/reports-download": reportsDownloadInfo,
  "/breakinlog": breakinLogInfo,
  "/compare-tool": compareToolInfo,
  "/soundmouse": soundMouseInfo,
  "/admin": adminInfo,
};

export const getPageInfoContent = (pathname: string): PageInfoContent | undefined => pageInfoByPath[pathname];
